import React, { useState, useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Building2, Plus, Search } from 'lucide-react';
import { useAllBusinessesCrud, useBusinessById, useBusinessCategories, type Business } from '../hooks/useBusinessCrud';
import { TableContent } from './LayoutPage';
import { useBusinessContext } from '../context/BusinessContext';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { S3Image } from '../components/S3Image';
import { getBusinessOpenState } from '../utils/businessHours';
import './businesses.css';
import { useIsPhone } from '../hooks/useIsPhone';
import { BusinessListPhone, type PhoneBusiness } from './BusinessListPhone';
import { useT } from '../i18n/admin';

type StatusFilter = 'all' | 'active' | 'inactive';

const FILTER_CHIPS: Array<{ id: StatusFilter; label: string; dotClass?: string }> = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active', dotClass: 'bz-dot-active' },
  { id: 'inactive', label: 'Inactive', dotClass: 'bz-dot-inactive' },
];

export const BusinessList: React.FC = () => {
  const { business, loading: businessLoading } = useBusinessContext();
  const businessId = business?.id || null;
  const permissions = useUserPermissions();
  const isAdmin = permissions.isAdmin;
  // The admin and the tenant Front Desk (no business of its own) list every business of the tenant.
  const seesAll = permissions.seesAllBusinesses;

  const { data: allBusinesses, isLoading: allLoading } = useAllBusinessesCrud();
  const { data: myBusinesses, isLoading: myLoading } = useBusinessById(seesAll ? null : businessId);
  const { data: categories } = useBusinessCategories();

  const businesses = seesAll ? allBusinesses : myBusinesses;
  // Still loading while the groups or the caller's own business are unknown: otherwise the
  // page renders empty for a moment before the data arrives.
  const isLoading = permissions.isLoading || (seesAll ? allLoading : businessLoading || myLoading);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const navigate = useNavigate();
  // SAM-944 (M5): the list is view only on a phone, so there is no «New business» (its page is desktop only).
  const isPhone = useIsPhone();
  const { t } = useT();

  const displayBusinesses = useMemo(() => {
    if (!businesses) return [];
    let list = businesses;
    if (statusFilter === 'active') list = list.filter((b) => b.is_active);
    else if (statusFilter === 'inactive') list = list.filter((b) => !b.is_active);
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(
        (b) =>
          b.name?.toLowerCase().includes(q) ||
          b.description_es?.toLowerCase().includes(q) ||
          b.email?.toLowerCase().includes(q) ||
          b.phone_number?.toLowerCase().includes(q),
      );
    }
    // Alphabetical, accent- and case-insensitive (SAM-161). Trim to survive
    // dirty data (e.g. a name stored as " TRS Cap Cana " sorted first).
    // Grouping by status happens downstream, so sorting here orders both groups.
    return [...list].sort((a, b) =>
      (a.name || '').trim().localeCompare((b.name || '').trim(), undefined, { sensitivity: 'base' }),
    );
  }, [businesses, statusFilter, searchTerm]);

  const grouped = useMemo(() => {
    const map: Record<'active' | 'inactive', Business[]> = { active: [], inactive: [] };
    displayBusinesses.forEach((b) => {
      if (b.is_active) map.active.push(b);
      else map.inactive.push(b);
    });
    return map;
  }, [displayBusinesses]);

  // SAM-198: a business opens as its own page (/business/:id), not a side panel.
  const handleSelect = (biz: Business) => navigate(`/business/${biz.id}`);
  const handleAdd = () => navigate('/business/new');

  const avatar = (biz: Business, size = 36) =>
    biz.logo_url ? (
      <S3Image
        imageKey={biz.logo_url}
        alt={biz.name || 'Logo'}
        style={{ width: size, height: size, objectFit: 'contain' }}
      />
    ) : (
      <Building2 size={Math.round(size / 2)} color="var(--ink-400)" />
    );

  const dash = <span style={{ color: 'var(--ink-400)' }}>—</span>;

  // Category comes from business_category_id → /business_category (real data).
  const categoryName = (biz: Business) => {
    const id = biz.business_category_id;
    if (id == null) return null;
    return categories?.find((c) => c.id === id)?.name || null;
  };

  // ----- Shared header (title, count, add, search, filter chips) -----
  const listHeader = (
    <header className="bz-list-header">
      <div className="bz-list-title-row">
        <h1 className="bz-list-title">Businesses</h1>
        <div className="bz-title-right">
          {!isLoading && (businesses?.length ?? 0) > 0 && (
            <span className="bz-list-count">{businesses?.length}</span>
          )}
          {isAdmin && !isPhone && (
            <button type="button" className="bz-add-btn" onClick={handleAdd}>
              <Plus size={14} />
              New business
            </button>
          )}
        </div>
      </div>

      {seesAll && (
        <>
          <div className="bz-search">
            <span className="bz-search-icon"><Search size={14} /></span>
            <input
              className="bz-search-input"
              placeholder="Search businesses…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="bz-chips">
            {FILTER_CHIPS.map((chip) => (
              <button
                key={chip.id}
                type="button"
                className={`bz-chip ${statusFilter === chip.id ? 'is-active' : ''}`}
                onClick={() => setStatusFilter(chip.id)}
              >
                {chip.dotClass && <span className={`bz-chip-dot ${chip.dotClass}`} />}
                {chip.label}
              </button>
            ))}
          </div>
        </>
      )}
    </header>
  );

  // ----- Full-width table row (collapsed mode) -----
  const renderWideRow = (biz: Business) => {
    const openState = getBusinessOpenState(biz);
    return (
      <tr
        key={biz.id}
        onClick={() => handleSelect(biz)}
        style={{ cursor: 'pointer' }}
      >
        <td>
          <div className="bz-wide-name">
            <span className="bz-item-avatar" style={{ width: 32, height: 32 }}>
              {avatar(biz, 32)}
            </span>
            <span className="bz-item-name">{biz.name || 'Unnamed'}</span>
          </div>
        </td>
        <td>{categoryName(biz) || dash}</td>
        <td>
          {openState ? (
            <span className={`bz-open ${openState.state === 'open' ? 'is-open' : 'is-closed'}`} style={{ margin: 0 }}>
              <span className="bz-open-dot" />
              {openState.state === 'open' ? 'Open' : 'Closed'} · {openState.label}
            </span>
          ) : (
            dash
          )}
        </td>
        <td>{biz.email || dash}</td>
        <td>{biz.phone_number || dash}</td>
        <td>
          <span className={`bz-item-status-pill ${biz.is_active ? 'active' : 'inactive'}`}>
            {biz.is_active ? 'Active' : 'Inactive'}
          </span>
        </td>
      </tr>
    );
  };

  // ----- Full-width table body (collapsed mode) -----
  const wideTableBody = isLoading ? (
    <div className="bz-list-empty">Loading businesses…</div>
  ) : displayBusinesses.length === 0 ? (
    <div className="bz-list-empty">
      <div>No businesses found</div>
      {!searchTerm && isAdmin && (
        <div style={{ fontSize: 12, marginTop: 4, color: 'var(--ink-400)' }}>
          Click "New business" to create your first business.
        </div>
      )}
    </div>
  ) : (
    <div className="biz-wide">
      <table className="bz-access-table bz-wide-table">
        <thead>
          <tr>
            <th>Business</th>
            <th>Category</th>
            <th>Hours</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {grouped.active.length > 0 && (
            <>
              <tr className="bz-wide-group-row">
                <td colSpan={6} className="bz-group-label">Active · {grouped.active.length}</td>
              </tr>
              {grouped.active.map(renderWideRow)}
            </>
          )}
          {grouped.inactive.length > 0 && (
            <>
              <tr className="bz-wide-group-row">
                <td colSpan={6} className="bz-group-label">Inactive · {grouped.inactive.length}</td>
              </tr>
              {grouped.inactive.map(renderWideRow)}
            </>
          )}
        </tbody>
      </table>
    </div>
  );

  // A business's own admin has one business: straight to its page, no list.
  // Wait for the Cognito groups first: isAdmin is false until they load.
  if (!permissions.isLoading && !seesAll && businessId) return <Navigate to={`/business/${businessId}`} replace />;

  // SAM-1308 (canvas BusinessesMobile): on a phone the list is cards, not the squeezed table.
  if (isPhone) {
    const phoneBusinesses: PhoneBusiness[] = displayBusinesses.map((b) => {
      const open = getBusinessOpenState(b);
      return {
        id: b.id,
        name: b.name?.trim() || t('phoneLists.business.unnamed'),
        logoKey: b.logo_url,
        category: categoryName(b),
        hours: open ? t(open.state === 'open' ? 'phoneLists.business.open' : 'phoneLists.business.closed', { label: open.label }) : null,
        active: !!b.is_active,
      };
    });
    return (
      <BusinessListPhone
        businesses={phoneBusinesses}
        loading={isLoading}
        count={businesses?.length ?? 0}
        query={searchTerm}
        onQuery={setSearchTerm}
        filter={statusFilter}
        onFilter={setStatusFilter}
        onOpen={(id) => navigate(`/business/${id}`)}
      />
    );
  }

  return (
    <TableContent>
      <div className="bz-app is-collapsed">
        <section className="bz-list-pane bz-list-pane--wide">
          {listHeader}
          <div className="bz-list-scroll">{wideTableBody}</div>
        </section>
      </div>
    </TableContent>
  );
};
